import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Cat — a chubby ginger house cat sitting on its haunches, storybook style:
// a round pear of a body, a huge friendly head with big close-set eyes,
// pointy ears and a tail curled around its feet. Faces +Z, sits on y = 0.
//
// Rig layout (flat — each named node moves about its own pivot):
//   - "body" : haunches + chest + belly. Breathes (squash/stretch about the
//              ground contact) so the cat never looks dead.
//   - "head" : the whole head assembly (ears, muzzle, eyes). Bobs and tilts
//              about the neck.
//   - "tail" : the curled tail. Wags about its root at the back of the body.
// The planted front legs and paws are static (unnamed).

const GINGER = "#e8963f";      // warm ginger coat
const GINGER_DARK = "#c9772a"; // shaded haunches / stripes / ear backs
const CREAM = "#f6efe3";       // belly, muzzle, paws, tail tip
const PINK = "#e8a0a0";        // inner ears
const NOSE = "#d97878";        // the little triangle nose
const EYE_WHITE = "#f7f3e8";   // eye whites (never pure white)
const PUPIL = "#3a2b20";       // warm dark pupils (never pure black)

// The pivots the animations move the named nodes about.
const BODY_PIVOT: [number, number, number] = [0, 0, 0];          // ground contact
const HEAD_PIVOT: [number, number, number] = [0, 0.30, 0.03];    // the neck
const TAIL_PIVOT: [number, number, number] = [0, 0.05, -0.13];   // tail root

// One big friendly eye: white ball, dark pupil slightly forward, tiny highlight.
function eye(x: number): Part[] {
  return [
    { geometry: new THREE.SphereGeometry(0.036, 10, 8), color: EYE_WHITE,
      position: [x, 0.435, 0.155], name: "head" },
    { geometry: new THREE.SphereGeometry(0.018, 8, 6), color: PUPIL,
      position: [x, 0.435, 0.183], name: "head" },
    { geometry: new THREE.SphereGeometry(0.007, 6, 5), color: CREAM,
      position: [x + 0.008, 0.445, 0.196], name: "head" },
  ];
}

// One pointy ear: a ginger cone with a pink inner cone, tilted outward.
function ear(side: 1 | -1): Part[] {
  const x = side * 0.085;
  return [
    { geometry: new THREE.ConeGeometry(0.05, 0.10, 5), color: GINGER_DARK,
      position: [x, 0.545, 0.02], rotation: [0, 0, -side * 0.24], name: "head" },
    { geometry: new THREE.ConeGeometry(0.028, 0.06, 5), color: PINK,
      position: [x, 0.535, 0.038], rotation: [0, 0, -side * 0.24], name: "head" },
  ];
}

export const cat = defineModel({
  id: "cat",
  batchId: "mess",
  name: "Cat",
  tags: ["mess", "cute", "animated", "creature", "animal", "cat", "pet", "mammal"],
  summary: "A chubby ginger house cat sitting with its tail curled around its feet.",
  description:
    "A storybook house cat sitting on its haunches: a round ginger body with a " +
    "cream belly, a huge head with big close-set eyes and pointy ears, planted " +
    "front paws and a tail curled around the base. Breathes at rest (idle) and " +
    "wags its curled tail (tail-wag).",
  author: "models",
  footprint: 0.3,
  tiles: { w: 1, h: 1 },
  parts(): Part[] {
    return [
      // ── body (named, breathes) ──────────────────────────────────────────
      // Haunches: the wide pear base, sitting right on the floor.
      { geometry: new THREE.SphereGeometry(0.16, 12, 10), color: GINGER,
        position: [0, 0.16, -0.01], scale: [1.12, 1.0, 1.05], name: "body" },
      // Chest rising toward the head.
      { geometry: new THREE.SphereGeometry(0.115, 12, 10), color: GINGER,
        position: [0, 0.27, 0.05], name: "body" },
      // Cream belly / chest patch on the front.
      { geometry: new THREE.SphereGeometry(0.09, 10, 8), color: CREAM,
        position: [0, 0.22, 0.10], scale: [0.9, 1.05, 0.6], name: "body" },
      // A darker saddle over the back for shading.
      { geometry: new THREE.SphereGeometry(0.12, 10, 8), color: GINGER_DARK,
        position: [0, 0.21, -0.09], scale: [1.05, 0.85, 0.8], name: "body" },

      // ── front legs + paws (static, planted) ─────────────────────────────
      { geometry: new THREE.CylinderGeometry(0.035, 0.038, 0.17, 8), color: GINGER,
        position: [-0.06, 0.10, 0.125] },
      { geometry: new THREE.CylinderGeometry(0.035, 0.038, 0.17, 8), color: GINGER,
        position: [0.06, 0.10, 0.125] },
      { geometry: new THREE.SphereGeometry(0.045, 8, 6), color: CREAM,
        position: [-0.06, 0.04, 0.14], scale: [1, 0.8, 1.15] },
      { geometry: new THREE.SphereGeometry(0.045, 8, 6), color: CREAM,
        position: [0.06, 0.04, 0.14], scale: [1, 0.8, 1.15] },

      // ── head (named, bobs and tilts) ─────────────────────────────────────
      { geometry: new THREE.SphereGeometry(0.145, 14, 12), color: GINGER,
        position: [0, 0.40, 0.04], name: "head" },
      // Cream muzzle.
      { geometry: new THREE.SphereGeometry(0.06, 10, 8), color: CREAM,
        position: [0, 0.36, 0.15], scale: [1.25, 0.8, 0.75], name: "head" },
      // Pink triangle nose.
      { geometry: new THREE.ConeGeometry(0.018, 0.024, 4), color: NOSE,
        position: [0, 0.383, 0.196], rotation: [Math.PI, 0, Math.PI / 4], name: "head" },
      ...ear(1), ...ear(-1),
      ...eye(0.055), ...eye(-0.055),

      // ── tail (named, wags) ───────────────────────────────────────────────
      // A curled tail wrapping around the right side of the haunches.
      { geometry: new THREE.TorusGeometry(0.13, 0.032, 8, 16, 3.9), color: GINGER_DARK,
        position: [0.06, 0.045, -0.05], rotation: [Math.PI / 2, 0, 0], name: "tail" },
      // Cream tip resting near the front-left paw side.
      { geometry: new THREE.SphereGeometry(0.038, 8, 6), color: CREAM,
        position: [-0.033, 0.045, -0.14], name: "tail" },
    ];
  },
  animations: [
    // Idle — a slow breath: the body squashes/stretches about the ground while
    // the head rides it with a gentle bob and micro-tilt. Never looks dead.
    {
      id: "idle",
      label: "Idle breathing",
      duration: 3.0,
      loop: true,
      tracks: [
        {
          target: "body", pivot: BODY_PIVOT,
          scale: [
            { t: 0, value: [1, 1, 1], ease: "sine" },
            { t: 1.5, value: [1.015, 1.035, 1.015], ease: "sine" },
            { t: 3.0, value: [1, 1, 1], ease: "sine" },
          ],
        },
        {
          target: "head", pivot: HEAD_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.5, value: [0, 0.010, 0], ease: "sine" },
            { t: 3.0, value: [0, 0, 0], ease: "sine" },
          ],
          rotation: [
            { t: 0, value: [0.02, 0, 0.015], ease: "sine" },
            { t: 1.5, value: [-0.02, 0, -0.015], ease: "sine" },
            { t: 3.0, value: [0.02, 0, 0.015], ease: "sine" },
          ],
        },
        {
          target: "tail", pivot: TAIL_PIVOT,
          rotation: [
            { t: 0, value: [0, 0.04, 0], ease: "sine" },
            { t: 1.5, value: [0, -0.04, 0], ease: "sine" },
            { t: 3.0, value: [0, 0.04, 0], ease: "sine" },
          ],
        },
      ],
    },

    // Tail-wag — the signature move: the curled tail sweeps side to side about
    // its root while the head cocks along in sympathy.
    {
      id: "tail-wag",
      label: "Tail wag",
      duration: 1.8,
      loop: true,
      tracks: [
        {
          target: "tail", pivot: TAIL_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.45, value: [0, 0.4, 0], ease: "sine" },
            { t: 0.9, value: [0, 0, 0], ease: "sine" },
            { t: 1.35, value: [0, -0.4, 0], ease: "sine" },
            { t: 1.8, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "head", pivot: HEAD_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.45, value: [0, 0.08, -0.06], ease: "sine" },
            { t: 0.9, value: [0, 0, 0], ease: "sine" },
            { t: 1.35, value: [0, -0.08, 0.06], ease: "sine" },
            { t: 1.8, value: [0, 0, 0], ease: "sine" },
          ],
        },
      ],
    },
  ],
});
